'use client';

import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import styles from './IntegrationsScenariosBlock.module.scss';

export type IntegrationFilterOption = {
  value: string;
  label: string;
};

type IntegrationsFilterSelectProps = {
  fieldId: string;
  label: string;
  value: string;
  placeholderLabel: string;
  options: IntegrationFilterOption[];
  onChange: (value: string) => void;
};

export const IntegrationsFilterSelect: FC<IntegrationsFilterSelectProps> = (props) => {
  const { fieldId, label, value, placeholderLabel, options, onChange } = props;

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerId = `${fieldId}-trigger`;
  const listboxId = `${fieldId}-listbox`;

  const items = useMemo(() => [{ value: '', label: placeholderLabel }, ...options], [placeholderLabel, options]);

  const displayLabel =
    value === '' ? placeholderLabel : items.find((o) => o.value === value)?.label ?? placeholderLabel;

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeIfOutside = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel} id={`${fieldId}-label`} htmlFor={triggerId}>
        {label}
      </label>

      <div ref={rootRef} className={styles.dropdownRoot}>
        <button
          id={triggerId}
          type="button"
          className={styles.dropdownTrigger}
          aria-labelledby={`${fieldId}-label`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={styles.dropdownTriggerLabel}>{displayLabel}</span>
        </button>

        {open && (
          <ul id={listboxId} className={styles.dropdownList} role="listbox" aria-labelledby={`${fieldId}-label`}>
            {items.map((item) => (
              <li
                key={item.value === '' ? '__empty' : item.value}
                className={styles.dropdownListItem}
                role="presentation"
              >
                <button
                  type="button"
                  className={styles.dropdownOption}
                  role="option"
                  aria-selected={value === item.value}
                  onClick={() => {
                    onChange(item.value);
                    setOpen(false);
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
